import gql from "graphql-tag";

export const ADDRESS_FIELDS = gql`
  fragment AddressFields on AddressType {
    id
    name
    line1
    line2
    city
    postcode
    country {
      id
      isoCode
      name
    }
    phoneNumber
  }
`;

export const ORGANISATION_PATIENT_FIELDS = gql`
  fragment OrganisationPatientFields on OrganisationPatientType {
    id
    requiresGuardianConsent
    patientProfile {
      id
      pk
      gender
      dateOfBirth
      user {
        id
        firstName
        lastName
        email
        correspondenceEmail
        isUserActive
      }
      guardian {
        id
        user {
          id
          email
          firstName
          lastName
        }
      }
    }
  }
`;

export const ORGANISATION_CORE_FIELDS = gql`
  fragment OrganisationCoreFields on CmpOrganisationType {
    id
    patientRegistrationUrl
    isDefault
    additionalNotificationEmail
    additionalNotificationEmailForNotifications
    accountOnHold
    primaryAddress {
      ...AddressFields
    }
    secondaryAddress {
      ...AddressFields
    }
    hashId
    stripeConnectedAccountName
    stripeConnectedAccountAddress
    stripeCustomerAccount {
      id
      paymentMethods {
        id
        default
        methodType
        lastFour
        fingerprint
      }
    }
    name
    patientNotifications
    automaticInvoicingEnabled
    chargesEnabled
    onboardingUrl
    onboardingStatus
    missingRequirements
    contactNumber
    website
    companyNumber
    vatNumber
    permissions
    teamMembersEnabled
  }
  ${ADDRESS_FIELDS}
`;

export const ORGANISATION_FIELDS = gql`
  fragment OrganisationFields on CmpOrganisationType {
    ...OrganisationCoreFields
    patients {
      ...OrganisationPatientFields
    }
  }
  ${ORGANISATION_CORE_FIELDS}
  ${ORGANISATION_PATIENT_FIELDS}
`;

export const ORGANISATION_STOCK_ITEM_FIELDS = gql`
  fragment OrganisationStockItemFields on StockType {
    id
    omnosBarcodeId
    testProduct {
      id
      paymentIsoCode
      organisation
      name
      supplier {
        id
        name
      }
      providerApi
      isCyrex
      addons(excludeReportAddons: true) {
        id
        paymentIsoCode
        organisation
        name
        content {
          id
          description
        }
        tradeCurrentPrice
      }
    }
    addons {
      id
      paymentIsoCode
      organisation
    }
    createdFromOrder {
      id
      checkoutDate
    }
    testProductOption {
      id
      paymentIsoCode
      organisation
    }
    orderTestItem {
      id
      providerTestId
      providerItemId
    }
    registeredUser {
      id
      firstName
      lastName
      email
    }
  }
`;

export const STOCK_BASKET_FIELDS = gql`
  fragment StockBasketFields on OrganisationBasketType {
    id
    appliedTradeDiscount {
      id
      code
    }
    discountInvalidReason
    basketTotalPrice
    basketShippingCost
    basketRrpTotalPrice
    basketTotalDiscount
    currencySymbol
    productLineItems {
      id
      pricePerUnit
      addons {
        id
        pricePerUnit
      }
      stockItem {
        ...OrganisationStockItemFields
      }
    }
  }
  ${ORGANISATION_STOCK_ITEM_FIELDS}
`;

export const ORGANISATION_BASKET_LINE_ITEM_FIELDS = gql`
  fragment OrganisationBasketLineItemFields on OrganisationBasketLineItemType {
    id
    invalid
    invalidReason
    hasDutchTest
    product {
      id
      paymentIsoCode
      organisation
      name
      productCode
      overlappingProductIds
      isCyrex
      requiredAddons {
        id
        paymentIsoCode
        organisation
      }
      isGenesTest
    }
    productOption {
      id
      paymentIsoCode
      organisation
      testProductOptionFamily {
        name
      }
    }
    clinicLocation {
      id
      paymentIsoCode
      organisation
      address
      clinic {
        name
      }
    }
    quantity
    fullPricePerUnit
    rrpPerUnit
    pricePerUnit
    discountPerUnit
    clinicFeesPerUnit
    addons {
      id
      product {
        id
        paymentIsoCode
        organisation
        name
        overlappingProductIds
        isCyrex
      }
      quantity
      fullPricePerUnit
      rrpPerUnit
      pricePerUnit
      discountPerUnit
      invalidReason
    }
    patient {
      id
      pk
      dateOfBirth
      gender
      hrt
      birthControl
      postMenopausal
      user {
        id
        firstName
        lastName
        email
      }
    }
    originalSampleOrderTestItem {
      id
      providerItemId
      providerTestId
      sampleReceivedByLab
      isValidForCyrexSample
      order {
        id
      }
      registeredUser {
        id
      }
      cyrexAddonProducts {
        id
        name
        paymentIsoCode
        organisation
        overlappingProductIds
      }
    }
  }
`;

export const ORGANISATION_BASKET_APPLICABLE_AUTO_APPLY_DISCOUNTS_FIELDS = gql`
  fragment OrganisationBasketApplicableAutoApplyDiscounts on OrganisationBasketType {
    applicableAutoApplyDiscounts {
      discount {
        id
        code
        value
        validTo
        available
        used
      }
    }
  }
`;

export const ORGANISATION_BASKET_FIELDS = gql`
  fragment OrganisationBasketFields on OrganisationBasketType {
    id
    organisation {
      id
      name
    }
    organisationPatient {
      ...OrganisationPatientFields
    }
    multiplePatientsEnabled
    hasDutchTest
    patient {
      id
      pk
      dateOfBirth
      gender
      hrt
      birthControl
      postMenopausal
      user {
        id
        firstName
        lastName
        email
        isUserActive
      }
    }
    patientShippingAddress {
      ...AddressFields
    }
    shippingAddress {
      ...AddressFields
    }
    locale {
      id
      name
      isoCode
    }
    paymentLocale {
      id
      name
      isoCode
    }
    invalid
    currencyName
    currencySymbol
    payee
    customsConfirmation
    purchaseAsStock
    commissionPercentageSplit
    appliedTradeDiscount {
      id
      code
    }
    basketShippingCost
    basketOutboundShippingCost
    basketReturnShippingCost
    basketRrpTotalPrice
    basketTotalPrice
    basketTotalCommission
    basketTotalDiscount
    discountInvalidReason
    productLineItems {
      ...OrganisationBasketLineItemFields
    }
    supplementLineItems {
      id
      invalid
      invalidReason
      supplement {
        id
        name
      }
      quantity
      fullPricePerUnit
      rrpPerUnit
      pricePerUnit
    }
  }
  ${ADDRESS_FIELDS}
  ${ORGANISATION_PATIENT_FIELDS}
  ${ORGANISATION_BASKET_LINE_ITEM_FIELDS}
`;

export const ORGANISATION_ONBOARDING_STATUS = gql`
  query OrganisationOnboardingStatus($organisation: ID!) {
    connectedAccount(organisation: $organisation) {
      id
      missingRequirements
      onboardingStatus
    }
  }
`;

export const CONNECTED_USER_FRAGMENT = gql`
  fragment ConnectedUserFragment on OrganisationPatientType {
    id
    created
    email
    patientProfile {
      id
      pk
      user {
        id
        isActive
        email
        firstName
        lastName
      }
      isWard
      guardian {
        id
        user {
          id
          isActive
          email
          firstName
          lastName
        }
      }
    }
    invited
    granted
    isAdmin
    relatedResultsAccessLinkId
    hasShippingAddress
  }
`;

export const APPOINTMENT_FIELDS = gql`
  fragment AppointmentFields on AppointmentType {
    id
    organisation {
      id
    }
    patientProfile {
      id
      pk
      user {
        id
        firstName
        lastName
        email
      }
    }
    type {
      id
    }
    additionalInformation
    scheduleMeetingUrl
  }
`;
